import { useEffect, useMemo, useState } from 'react';
import { useActiveSinkId, useMasterVolume } from 'hooks';
import { LayoutType } from 'types';

import { AppSettingsContext } from './context';
import { SELECTED_LAYOUT_TYPE_KEY } from '../../constants';

interface AppSettingsProviderProps {
  children?: JSX.Element;
}

export function AppSettingsProvider({ children }: AppSettingsProviderProps) {
  const [activeSinkId, setActiveSinkId] = useActiveSinkId();
  const [masterVolume, setMasterVolume] = useMasterVolume();
  const [open, setOpen] = useState<boolean>(false);

  // Read the stored preference once on mount. Never write the default back — only an
  // explicit user selection should ever set this key, so Room.tsx can distinguish
  // a real user preference from an auto-switched layout.
  const storedLayout = window.localStorage.getItem(SELECTED_LAYOUT_TYPE_KEY);
  const [layoutType, setLayoutTypeState] = useState<LayoutType>(
    storedLayout ? (JSON.parse(storedLayout) as LayoutType) : LayoutType.SPEAKER,
  );

  const setLayoutType = (newLayout: LayoutType): void => {
    window.localStorage.setItem(SELECTED_LAYOUT_TYPE_KEY, JSON.stringify(newLayout));
    window.dispatchEvent(new Event('localStorage'));
    setLayoutTypeState(newLayout);
  };

  // Stay in sync when another component or tab writes to the same key.
  useEffect(() => {
    const handler = (): void => {
      const item = window.localStorage.getItem(SELECTED_LAYOUT_TYPE_KEY);
      setLayoutTypeState(item ? (JSON.parse(item) as LayoutType) : LayoutType.SPEAKER);
    };
    window.addEventListener('localStorage', handler);
    return () => window.removeEventListener('localStorage', handler);
  }, []);

  const isGalleryViewActive = useMemo(() => layoutType === LayoutType.GRID, [layoutType]);

  const openSettings = (): void => {
    setOpen(true);
  };

  const closeSettings = (): void => {
    setOpen(false);
  };

  return (
    <AppSettingsContext.Provider
      value={{
        activeSinkId,
        setActiveSinkId,
        masterVolume,
        setMasterVolume,
        layoutType,
        setLayoutType,
        isGalleryViewActive,
        open,
        openSettings,
        closeSettings,
      }}
    >
      {children}
    </AppSettingsContext.Provider>
  );
}
